import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '../../core/store';
import { createSkillTexture } from './SkillCanvas';
import type { SkillBoard } from '../../data/skillBoards';
import { loadSkillLogo, SKILL_LOGO } from './skillLogos';

const BOARD_W = 1.72;
const BOARD_H = 2.05;
const BOARD_Y = 1.42;
const WALL_X = 2.36;
const APPROACH_DIST = 3.6;
const UNLOCK_DIST = 2.35;

function paintTexture(
  texture: THREE.CanvasTexture,
  board: SkillBoard,
  logos: (CanvasImageSource | null)[] = [],
) {
  const canvas = createSkillTexture(
    board,
    {
      progress: board.progress,
      skillsVisible: board.skills.length,
      scanY: -1,
      brightness: 1,
      active: false,
    },
    logos,
  );
  const img = texture.image as HTMLCanvasElement;
  const ctx = img.getContext('2d');
  if (!ctx) return;
  if (img.width !== canvas.width || img.height !== canvas.height) {
    img.width = canvas.width;
    img.height = canvas.height;
  }
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(canvas, 0, 0);
  texture.needsUpdate = true;
}

export function SkillCard({
  board,
  fade = 1,
}: {
  board: SkillBoard;
  fade?: number;
}) {
  const group = useRef<THREE.Group>(null);
  const alreadyUnlocked = useGameStore((s) =>
    s.unlockedSkills.includes(board.id),
  );
  const celebrated = useRef(alreadyUnlocked);

  const texture = useMemo(() => {
    const canvas = createSkillTexture(board, {
      progress: board.progress,
      skillsVisible: board.skills.length,
      scanY: -1,
      brightness: 1,
      active: false,
    });
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    tex.flipY = true;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;
    return tex;
  }, [board]);

  useEffect(() => {
    let live = true;
    void Promise.all(
      board.skills.map((name) => loadSkillLogo(SKILL_LOGO[name] ?? '')),
    ).then((imgs) => {
      if (!live) return;
      paintTexture(texture, board, imgs);
    });
    return () => {
      live = false;
    };
  }, [board, texture]);

  useEffect(() => {
    return () => texture.dispose();
  }, [texture]);

  useEffect(() => {
    if (alreadyUnlocked) celebrated.current = true;
  }, [alreadyUnlocked]);

  const x = board.wall === 'left' ? -WALL_X : WALL_X;
  const rotY = board.wall === 'left' ? Math.PI / 2 : -Math.PI / 2;

  useFrame((state) => {
    if (!group.current || fade < 0.05) return;
    const camZ = state.camera.position.z;
    const dist = Math.abs(camZ - board.z);
    const near = dist < APPROACH_DIST && camZ > board.z - 1.4;
    if (near && dist < UNLOCK_DIST) celebrated.current = true;
    group.current.visible = fade > 0.05;
  });

  if (fade < 0.05) return null;

  return (
    <group ref={group} position={[x, BOARD_Y, board.z]} rotation={[0, rotY, 0]}>
      <mesh position={[0, 0, -0.06]}>
        <boxGeometry args={[BOARD_W + 0.14, BOARD_H + 0.14, 0.1]} />
        <meshStandardMaterial
          color="#3A342C"
          metalness={0.82}
          roughness={0.32}
        />
      </mesh>
      <mesh position={[0, 0, -0.01]}>
        <planeGeometry args={[BOARD_W + 0.05, BOARD_H + 0.05]} />
        <meshStandardMaterial
          color={board.color}
          emissive={board.color}
          emissiveIntensity={0.22}
          metalness={0.7}
          roughness={0.4}
        />
      </mesh>
      <mesh position={[0, 0, 0.02]}>
        <planeGeometry args={[BOARD_W, BOARD_H]} />
        <meshBasicMaterial
          map={texture}
          toneMapped={false}
          polygonOffset
          polygonOffsetFactor={-1}
          polygonOffsetUnits={-1}
        />
      </mesh>
      <spotLight
        position={[0.15, 0.4, 1.1]}
        angle={0.55}
        penumbra={0.85}
        intensity={1.7}
        distance={2.8}
        color="#F2E6D0"
        castShadow={false}
      />
    </group>
  );
}
